import React, { useCallback, useEffect, useState } from 'react'
import DocumentHeader from '../../../../documents/components/DocumentHeader'
import { useParams } from 'react-router-dom'
import { Box, Card, Typography } from '@mui/material';
import CommonAccountTable from '../../../../documents/components/CommonAccountTable';
import { AccountTableColumn } from '../../../../types';
import { request } from '../../../../../../utils/request';
import { toast } from 'react-toastify';
import PrintButtons from '../../../../../../components/PrintButtons';
import usePrint from '../../../../../../utils/usePrint';
import PrintableContainer from '../../../../../../components/PrintableContainer';

const ShowPaymentIn = () => {
  const { paymentInId } = useParams();
  const [loading, setLoading] = useState(true);
  const [singlePaymentIn, setSinglePaymentIn] = useState<any | null>({});

  const { isPrinting, handlePrint } = usePrint();

  const onPrint = () => {
    // force re-render before invoking print
    requestAnimationFrame(() => {
      setTimeout(() => {
        handlePrint('payment_in_print', {
          title: `Payment In - ${singlePaymentIn?.paymentInNumber}'}`,
          fileName: `Sales _${singlePaymentIn?.paymentInNumber}`,
          landscape: false,
          showHeader: true,
          showFooter: false,
          customStyles: `
          table { width: 100% !important; border-collapse: collapse !important; }
          th, td { border: 1px solid #ddd !important; padding: 6px !important; }
          th { background-color: #f5f5f5 !important; font-weight: bold !important; }
          .no-print { display: none !important; }
          .invoice-heading {text-align:center; font-size: 22px}
          .flex-center {display:flex; justify-content:space-between; align-items: flex-start; gap:5px}
          .sub-heading {font-size:14px; font-weight:bold; margin-bottom:0px}
          .sub-heading2 {font-size:12px; font-weight:bold}
          .text-bold {font-weight:bold}
          .w-50{width:50%}
          .out-border{border:1px solid black}
        `
        });
      }, 150); // small delay ensures browser repaints
    });
  };

  const fetchSinglePaymentIn = useCallback(async () => {
    if (!paymentInId) return;

    setLoading(true)
    try {
      const res = await request({
        url: `paymentIn/get-single-payment-in/${paymentInId}`,
        method: "get",
      })
      if (res.success && res.data) {
        setSinglePaymentIn(res.data)
      } else {
        toast.error(res.message || "Failed to fetch Payment in");
      }
    } catch (error: any) {
      toast.error(error.message || "Error fetching Payment in");
    } finally {
      setLoading(false)
    }
  }, [paymentInId])

  useEffect(() => {
    fetchSinglePaymentIn();
  }, [fetchSinglePaymentIn])

  // Define columns for the table
  const columns: AccountTableColumn[] = [
    {
      id: 'paymentInDate',
      label: 'DATE',
      minWidth: 100,
      renderCell: (row) => {
        if (!row.paymentInDate) return 'N/A';
        try {
          return new Date(row.paymentInDate).toLocaleDateString('en-GB');
        } catch (e) {
          return 'Invalid Date';
        }
      }
    },
    {
      id: 'invoiceNumber',
      label: 'INVOICE NUMBER',
      minWidth: 200,
      renderCell: (row) => {
        if (row.invoiceId && typeof row.invoiceId === 'object') {
          return row.invoiceId.invoiceNumber || '-';
        }
        return '-';
      }
    },
    {
      id: 'totalAmount',
      label: 'INVOICE AMOUNT (₹)',
      minWidth: 120,
      renderCell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 600 }}>
            {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
            }).format(row.invoiceId?.totalAmount || 0)}
          </span>
        </div>
      )
    },
    {
      id: 'discount',
      label: 'DISCOUNT (₹)',
      minWidth: 120,
      renderCell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 600 }}>
            {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
            }).format(row.invoiceId?.discount?.amount || 0)}
          </span>
        </div>
      )
    },
    {
      id: 'receivedAmount',
      label: 'AMOUNT RECEIVED',
      minWidth: 120,
      renderCell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 600 }}>
            {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
            }).format(row.receivedAmount || 0)}
          </span>
        </div>
      )
    },
    {
      id: 'balanceAmount',
      label: 'BALANCE AMOUNT (₹)',
      minWidth: 120,
      renderCell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 600 }}>
            {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
            }).format(row.invoiceId?.balanceAmount || 0)}
          </span>
        </div>
      )
    }
  ];

  const detailBox = (label: any, value?: any | null) => {
    return (
      <Box flex={4} sx={{ display: 'flex', flexDirection: "column", gap: .8 }}>
        <Typography variant='h6' sx={{ fontSize: '0.78rem', fontWeight: 600 }} className='sub-heading2'>
          {label}
        </Typography>
        <Typography variant='body1' sx={{ fontSize: '0.75rem' }}>
          {value || '--'}
        </Typography>
      </Box>
    )
  }

  // If still loading
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography>Loading Payment In details...</Typography>
      </Box>
    );
  }

  // If no data found
  if (!singlePaymentIn) {
    return (
      <>
        <DocumentHeader heading='Payment In' showBackButton={true} />
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography>No payment in data found</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentHeader
        heading='Payment In'
        subtitle={`#${singlePaymentIn?.paymentInNumber}`}
        showBackButton={true}
      />

      <Box display='flex' justifyContent='flex-end' mb={3} gap={2}>
        <PrintButtons
          onPrint={onPrint}
          isPrinting={isPrinting}
          printLabel='Print PI'
          pdfLabel='Export PDF'
          disabled={loading || !singlePaymentIn}
          showPDF={false} // Remove PDF button since we don't have export functionality yet
        />
      </Box>

      {/* Payment Details Section */}
      <PrintableContainer id="payment_in_print">
        <Box mb={4}>
          <Typography variant='h6' mb={1} className='sub-heading'>
            Payment Details
          </Typography>
          <Card sx={{ p: 2, borderRadius: 1.2 }}>
            <Box display='flex' gap={3} mb={3} flexWrap="wrap" className="flex-center">
              {detailBox("Party Name", singlePaymentIn?.party?.partyName || singlePaymentIn?.party?.nickName)}
              {detailBox("Payment Date", singlePaymentIn?.paymentInDate ? new Date(singlePaymentIn.paymentInDate).toLocaleDateString('en-GB') : '--')}
              {detailBox("Amount Received (₹)", singlePaymentIn?.receivedAmount ?
                new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(singlePaymentIn.receivedAmount)
                : '₹0')}
              {detailBox("Payment Discount (₹)", singlePaymentIn?.discount?.amount ?
                new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(singlePaymentIn.discount.amount)
                : '₹0')}
              {detailBox("Payment Mode", singlePaymentIn?.paymentType)}
              {detailBox("GST Type", singlePaymentIn?.gstType)}
              {/* {detailBox("Settled Amount (₹)", singlePaymentIn?.settledAmount ?
                new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(singlePaymentIn.settledAmount)
                : '₹0')} */}
            </Box>

            {/* Notes Section */}
            <Box mt={2}>
              {detailBox("Notes:", singlePaymentIn?.notes)}
            </Box>
          </Card>
        </Box>

        {/* Invoice Details Section - Using CommonAccountTable */}
        <Box mb={4}>
          <Typography variant='h6' mb={0} className='sub-heading'>
            Invoice Settled with this Payment
          </Typography>

          {singlePaymentIn ? (
            <CommonAccountTable
              columns={columns}
              data={[singlePaymentIn]} // Wrap single object in array
              loading={false}
              idField='_id'
              getRowLink={(row: any) => `/accounts/sales/salesInvoice/${row?.invoiceId?._id}`}
              paginationEnabled={false}
              showDateRange={false}
              showCreateButton={false}
              showSearchBar={false}
              showFilter={false}
            />
          ) : (
            <Card sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No invoice linked to this payment
              </Typography>
            </Card>
          )}
        </Box>
      </PrintableContainer>

      {/* Payment Summary Stats */}
      {/* <Box mb={4}>
        <Typography variant='h6' mb={2}>
          Payment Summary
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Card sx={{ p: 2, flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Total Invoice Amount
            </Typography>
            <Typography variant="h5" fontWeight={600}>
              {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
              }).format(singlePaymentIn.invoice?.totalAmount || 0)}
            </Typography>
          </Card>
          
          <Card sx={{ p: 2, flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Total Received
            </Typography>
            <Typography variant="h5" fontWeight={600} color="success.main">
              {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
              }).format(singlePaymentIn.receivedAmount || 0)}
            </Typography>
          </Card>
          
          <Card sx={{ p: 2, flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Remaining Balance
            </Typography>
            <Typography variant="h5" fontWeight={600} color="warning.main">
              {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
              }).format(singlePaymentIn.invoice?.balanceAmount || 0)}
            </Typography>
          </Card>
          
          <Card sx={{ p: 2, flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Discount Applied
            </Typography>
            <Typography variant="h5" fontWeight={600} color="info.main">
              {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
              }).format(singlePaymentIn.discount?.amount || 0)}
            </Typography>
          </Card>
        </Box>
      </Box> */}
    </>
  )
}

export default ShowPaymentIn